import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import { TagOption, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { BadgeWithIconProps } from "../../../../data-model/badgeWithIconProps";
import { ScreenNamesForBigQuery } from "../../../../../shared/enums/eventsData";
import { EventsName } from "../../../../../shared/enums/eventsName";
import { CptCodeWithDetailsKeys } from "../../../../../shared/enums/tableColumnKeys";
import {
  getAllJCodesFromAuthOnFile,
  jCodeRowLabelForAnalytics,
} from "../../../../pages/singleOrderLayout/utils/existingDocsToLs";
import { RootState } from "../../../../redux/store/store";
import { checkIfBusinessOfficeNotesAreEmpty } from "../../../../utils/busniessOfficeNotesHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import SelectableBadge from "../../../selectableBadge/selectableBadge";

/**
 * Cell props for rendering selectable badge/tag table controls.
 */
interface SelectableBadgeCellProps {
  value: BadgeWithIconProps & {
    onClick?: (badgeId: any, rowIndex: number) => void;
  };
  rowIndex: number;
  header: string;
  onValueChange?: (data: any) => void;
  onTableRowUpdate?: (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => void;

  [key: string]: any;
}

/**
 * Maps badge options into Oasis tag option objects.
 *
 * @param badges - Available badge options for the dropdown
 * @returns Oasis tag options for the Tags component
 */
const toTagOptions = (badges: BadgeWithIconProps[]): TagOption[] => {
  return badges.map((badge) => ({
    label: badge.text,
    value: badge.id,
    backgroundColor: badge.bgColor,
    textColor: badge.textColor,
  }));
};

/**
 * Renders a selectable badge control in table cells.
 *
 * @param props - Table cell value, row metadata, and update callbacks
 * @returns Badge dropdown for generic tables, and Oasis Tags dropdown for CPT auth status
 */
export const SelectableBadgeCell: React.FC<SelectableBadgeCellProps> = ({
  value,
  rowIndex,
  header,
  onValueChange,
  onTableRowUpdate,
}) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { user } = useAuthSelector((state) => state.firebaseAuthentication);
  const { generalOrderDetails, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const valueBadgeList = value ? (value as any)?.badgeList : undefined;

  // Order assignment configuration columns that should'nt use auth status options
  // These columns have their own custom badge lists
  const orderAssignmentColumns = [
    "medical_order_status", // Type column
    "if_multi_regimen", // Multi Agent column
  ];

  const isOrderAssignmentColumn = orderAssignmentColumns.includes(header);
  const isCptAuthStatusColumn = header === CptCodeWithDetailsKeys.PaRequired;

  const badgeList =
    valueBadgeList !== undefined && valueBadgeList !== null
      ? Array.isArray(valueBadgeList)
        ? valueBadgeList
        : []
      : isOrderAssignmentColumn
        ? [] // Don't use auth status options for order assignment columns
        : (authStatusOptions ?? []);

  /**
   * Fires BigQuery analytics when the CPT auth-status Tags trigger is activated
   * (opens the menu), matching SelectableBadge / BadgeDropdown onDefaultBadgeClick.
   */
  const handleCptTagsTriggerClick = useCallback(() => {
    const jCode = getAllJCodesFromAuthOnFile(
      authOnFileDetails?.auth_on_file ?? undefined,
    );
    const jCodeLabel = jCodeRowLabelForAnalytics(jCode, rowIndex);
    apiWorkerManager.enqueueAndProcess({
      patient_id: generalOrderDetails?.order?.demographics?.patient_id ?? "",
      order_id: generalOrderDetails?.order?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() || "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name:
        EventsName.AUTH_STATUS_DROPDOWN_CLICKED + " (" + jCodeLabel + ")",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
        j_code: jCode?.[rowIndex ?? 0]?.j_code ?? "",
        drug_name: jCode?.[rowIndex ?? 0]?.medicine_name ?? "",
        payer_name: payerName,
        j_code_auth_status: jCode?.[rowIndex ?? 0]?.auth_status ?? "",
      },
    });
  }, [
    authOnFileDetails?.auth_on_file,
    generalOrderDetails?.order,
    payerName,
    rowIndex,
    user?.email,
    user?.id,
  ]);

  /**
   * Handles CPT auth-status option selection: callbacks, row update, and
   * AUTH_STATUS_DROPDOWN_SELECTED analytics (SelectableBadge parity).
   */
  const handleCptTagsSelect = useCallback(
    (option: TagOption) => {
      const typedList = badgeList as BadgeWithIconProps[];
      const selectedBadge = typedList.find((b) => b.id === option.value);
      if (!selectedBadge) {
        return;
      }

      value?.onClick?.(option.value, rowIndex);
      onValueChange?.(option.label);

      const jCode = getAllJCodesFromAuthOnFile(
        authOnFileDetails?.auth_on_file ?? undefined,
      );
      const jCodeLabel = jCodeRowLabelForAnalytics(jCode, rowIndex);
      apiWorkerManager.enqueueAndProcess({
        patient_id: generalOrderDetails?.order?.demographics?.patient_id ?? "",
        order_id: generalOrderDetails?.order?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name:
          EventsName.AUTH_STATUS_DROPDOWN_SELECTED +
          " (" +
          jCodeLabel +
          " - " +
          selectedBadge.text +
          ")",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
          old_value: value?.text ?? "",
          new_value: selectedBadge.text,
          payer_name: payerName,
          j_code: jCode?.[rowIndex ?? 0]?.j_code ?? "",
          bo_empty: checkIfBusinessOfficeNotesAreEmpty(
            authOnFileDetails?.auth_on_file?.reports ?? [],
          ).toString(),
          drug_name: jCode?.[rowIndex ?? 0]?.medicine_name ?? "",
          j_code_auth_status: jCode?.[rowIndex ?? 0]?.auth_status ?? "",
        },
      });

      onTableRowUpdate?.(rowIndex, header, {
        ...value,
        text: option.label,
        textColor: option.textColor,
        bgColor: option.backgroundColor,
        currentStatusId: option.value,
      });
    },
    [
      authOnFileDetails?.auth_on_file,
      badgeList,
      generalOrderDetails?.order,
      header,
      onTableRowUpdate,
      onValueChange,
      payerName,
      rowIndex,
      user?.email,
      user?.id,
      value,
    ],
  );

  if (isCptAuthStatusColumn) {
    return (
      <div className="table-cell-selectable-badge flex w-full flex-row items-center gap-2">
        <Tags
          key={`${value?.id ?? ""}-${value?.text ?? ""}-${Math.random()}`}
          label={value?.text ?? ""}
          backgroundColor={value?.bgColor ?? "#F5F5F5"}
          textColor={value?.textColor ?? "#0F0F0F"}
          options={toTagOptions(badgeList as BadgeWithIconProps[])}
          shouldUsePortal={true}
          onClick={handleCptTagsTriggerClick}
          onSelect={handleCptTagsSelect}
        />
      </div>
    );
  }

  return (
    <>
      <div className="table-cell-selectable-badge flex w-full flex-row items-center gap-2">
        <SelectableBadge
          selectedBadge={value}
          badgeList={badgeList}
          onClick={(badgeId) => {
            if (value.onClick) {
              value.onClick(badgeId, rowIndex);
            }
          }}
          onValueChange={(newValue) => {
            if (onValueChange) {
              onValueChange(newValue);
            }
          }}
          fieldKey={header}
          rowIndex={rowIndex}
          onTableRowUpdate={onTableRowUpdate}
          shouldUsePortal={true}
        />
      </div>
    </>
  );
};

export default SelectableBadgeCell;
